import React, { useMemo } from "react";

import PropTypes from "prop-types";

import IconButton from "@cayuse/shared-react.components.icon-button";
import { crunchClasses } from "@cayuse/shared-react.utils.helpers";

import { EffortInput } from "components/BudgetBuilder/ResearchTeam/EffortInput";
import { PersonMonthsInput } from "components/BudgetBuilder/ResearchTeam/PersonMonthsInput";
import { useTenantFormatter } from "components/BudgetBuilder/shared/TenantFormatter";
import Table from "components/Table";
import { MEMBER_TYPES } from "globals/constants";
import {
	formatNumberAsPercent,
	getFringeRequested,
	getRequestedSalary,
	isPresent,
	toNumber,
	withOnly1DecimalPlace,
} from "utils/budgetBuilder";

import { EditableTableCell } from "../shared/EditableTableCell";
import styles from "./styles.css";

const createDefaultDataSet = (category, isCostShare) => {
	let baseData = {
		baseSalary: category.baseSalary,
		apptMonths: category.apptMonths,
		effort: category.effort,
		personMonths: category.personMonths,
		fringeRate: category.fringeRate,
		reqSalary: category.reqSalary,
	};

	if (isCostShare) {
		baseData.unit = category.association || category?.associations?.[0]?.association;
		baseData.requirement = null;
		baseData.type = null;
	}

	return baseData;
};

const ResearchTeamRow = (
	{
		category,
		isCostShare,
		isEditing,
		isAddedMember,
		handleRemoveItem,
		handleRemoveUnnamedMember,
		hasFringeRate,
		updateResearchTeamMember,
		...rowProps
	},
) => {
	const { currencyFormatter } = useTenantFormatter();
	const canRemove = isEditing && isAddedMember;
	const {
		id,
		member,
		roleLabel,
		name,
		associationLabel,
		type,
	} = category;
	const classNames = crunchClasses({
		[styles.row]: true,
		[styles.parentRow]: true,
	});

	const { fringeRate, personMonths, baseSalary, effort, reqSalary, apptMonths } = useMemo(
		() => category?.[isCostShare ? "costShare" : "sponsoredCosts"] || createDefaultDataSet(category, isCostShare),
		[ category, isCostShare ],
	);

	const handleUpdate = update => {
		const updateKey = isCostShare ? "costShare" : "sponsoredCosts";
		updateResearchTeamMember({
			...category,
			[updateKey]: {
				...category?.[updateKey] || createDefaultDataSet(category, isCostShare),
				...update,
			},
		});
	};

	const handleEffortChange = effort => {
		let newMember = {};
		newMember.effort = effort;
		if (isPresent(effort)) {
			const newPersonMonths = toNumber(apptMonths) * (effort / 100);
			newMember.personMonths = newPersonMonths.toFixed(1);
			if (isPresent(baseSalary)) {
				newMember.reqSalary = baseSalary * (effort / 100);
			}
		}
		handleUpdate(newMember);
	};

	const handleFringeRateChange = fringeRate => {
		let newMember = { fringeRate };
		if (isPresent(reqSalary)) {
			newMember.fringeRequested = reqSalary * (fringeRate / 100);
		}
		handleUpdate(newMember);
	};

	const handleBaseSalaryChange = baseSalary => handleUpdate({ baseSalary: toNumber(baseSalary) });

	const handlePersonMonthsChange = personMonths => {
		let newCategory = {};
		newCategory.personMonths = personMonths;
		if (isPresent(personMonths)) {
			const newPersonMonths = withOnly1DecimalPlace(personMonths);
			newCategory.personMonths = newPersonMonths;
			const reducedApptMonths = toNumber(apptMonths);
			let newEffortValue = reducedApptMonths !== 0 ? ((newPersonMonths / (reducedApptMonths)) * 100) : 0;
			if (isNaN(newEffortValue)) {
				newEffortValue = 0;
			}
			newCategory.effort = Math.round(newEffortValue);
			newCategory.reqSalary = (baseSalary) * (effort / 100);
		}
		handleUpdate(newCategory);
	};

	const requestedSalary = useMemo(
		() => getRequestedSalary(category, isCostShare),
		[ category, isCostShare ]
	);

	const fringeRequested = useMemo(
		() => getFringeRequested(category, isCostShare),
		[ category, isCostShare ]
	);

	const periodTotal = useMemo(
		() => currencyFormatter(toNumber(requestedSalary) + toNumber(fringeRequested)),
		[ currencyFormatter, fringeRequested, requestedSalary ],
	);

	const personName = name ? name : (type === MEMBER_TYPES.MEMBER_UNNAMED ? "Unnamed Person" : "");

	const handleRemove = () => {
		if (type === MEMBER_TYPES.MEMBER_UNNAMED) {
			handleRemoveUnnamedMember(id);
		} else {
			handleRemoveItem(member);
		}
	};

	return (
		<Table.Row
			key={id}
			id={id}
			className={classNames}
			draggable={false}
			sort={category.sort}
			{...rowProps}
		>
			<Table.Cell className={styles.cellAlignLeft}>{personName}</Table.Cell>
			<Table.Cell className={styles.cellAlignLeft}>{roleLabel ? roleLabel : ""}</Table.Cell>
			<Table.Cell className={styles.cellAlignLeft}>{associationLabel ? associationLabel : ""}</Table.Cell>

			<EditableTableCell
				editable={isEditing}
				onChange={({ target: { value } }) => handleBaseSalaryChange(value)}
				value={baseSalary}
				inputStyles={isCostShare ? styles.inputBaseSalary : styles.input}
				textFormatter={currencyFormatter}
			/>
			<Table.Cell className={styles.cellAlignRight}>{apptMonths}</Table.Cell>
			<EditableTableCell
				editable={isEditing}
				onChange={({ target: { value } }) => handleEffortChange(value)}
				value={effort}
				inputStyles={styles.input}
				textFormatter={formatNumberAsPercent}
				renderInput={props => <EffortInput {...props} />}
			/>
			<EditableTableCell
				editable={isEditing}
				onChange={({ target: { value } }) => handlePersonMonthsChange(value)}
				value={personMonths}
				inputStyles={styles.inputBaseSalary}
				renderInput={props => <PersonMonthsInput {...props} />}
			/>
			<Table.Cell className={styles.cellAlignRight}>
				{currencyFormatter(requestedSalary)}
			</Table.Cell>
			<EditableTableCell
				editable={isEditing}
				onChange={({ target: { value } }) => handleFringeRateChange(value)}
				value={fringeRate}
				inputStyles={styles.input}
				textFormatter={formatNumberAsPercent}
			/>
			<Table.Cell className={styles.cellAlignRight}>
				{currencyFormatter(fringeRequested)}
			</Table.Cell>
			<Table.Cell className={styles.cellAlignRight}>
				<span className={styles.bold}>{periodTotal}</span>
			</Table.Cell>
			<Table.Cell className={styles.cellAlignCenter}>
				{ canRemove && (
					<IconButton
						tertiary
						onClick={handleRemove}
						className={styles.deleteButton}
						icon="trash"
					/>
				)}
			</Table.Cell>
		</Table.Row>
	);
};

ResearchTeamRow.defaultProps = {
	isCostShare: false,
};
ResearchTeamRow.propTypes = {
	category: PropTypes.shape({
		id: PropTypes.string,
		member: PropTypes.string,
		roleLabel: PropTypes.string,
		name: PropTypes.string,
		associationLabel: PropTypes.string,
		baseSalary: PropTypes.oneOfType([
			PropTypes.number,
			PropTypes.string,
		]),
		apptMonths: PropTypes.string,
		effort: PropTypes.number,
		personMonths: PropTypes.number,
		fringeRate: PropTypes.any,
		type: PropTypes.string,
	}),
	isCostShare: PropTypes.bool,
	isEditing: PropTypes.bool.isRequired,
	handleRemoveItem: PropTypes.func,
	handleRemoveUnnamedMember: PropTypes.func,
};

export default ResearchTeamRow;
